import type { PoseId } from '@/content/articulation';
import type { Choice } from '@/speech/understand/match';
import { shapeOf, type MouthShape, type ShotId } from '@/ui/tongue3d/TongueScene';
import { FULL_R, mistakeShape, partialR, type Mistake } from './labShapes';

/**
 * The Tongue Lab's question activities: the model shows a shape, asks one question, and the child
 * answers by voice or by tapping. Build the r: the child decides each part of the r in turn and
 * sees the tongue do it. Spot the mistake: an r with one part wrong; which part? Spin and spot:
 * the camera turns round the mouth; a yes/no or up/down question about what it shows. Facts from
 * docs/design/articulation-spec.md (bunched r).
 */

/** What the model shows: a shape, from where, and whether the camera turns round it. */
export interface Show {
  shape: MouthShape;
  shot: ShotId;
  /** Turn the camera round the mouth and back while the question is asked. */
  tour?: boolean;
  /** Mark the narrowest point under the roof. */
  gap?: boolean;
}

export interface Option extends Choice {
  label: string;
}

export interface Ask {
  id: string;
  show: Show;
  question: string;
  options: readonly Option[];
  answer: string;
  /** Said when the answer is right. */
  right: string;
  /** Said when it is not: what is true instead (no blame). */
  wrong: string;
  /** Shown after the answer (build: the tongue does the step). */
  after?: Show;
}

const UP: Option = { id: 'up', label: 'Up', says: ['up'] };
const DOWN: Option = { id: 'down', label: 'Down', says: ['down'] };
const YES: Option = { id: 'yes', label: 'Yes', says: ['yes'] };
const NO: Option = { id: 'no', label: 'No', says: ['no'] };

const at = (lift: number, brace: number, lips: number) => partialR({ lift, brace, lips });
const pose = (id: PoseId) => shapeOf(id);

/** Relaxed to the full r, one decision per part (the child's answer moves the tongue). */
export const BUILD: readonly Ask[] = [
  {
    id: 'middle',
    show: { shape: at(0, 0, 0), shot: 'side' },
    question: 'A relaxed tongue. For r, should the middle of the tongue go up or down?',
    options: [UP, DOWN],
    answer: 'up',
    right: 'Up it goes! The middle lifts toward the roof of the mouth.',
    wrong: 'It is already down when it is relaxed. For r the middle lifts up: watch.',
    after: { shape: at(0.7, 0, 0), shot: 'side' },
  },
  {
    id: 'touch',
    show: { shape: at(0.7, 0, 0), shot: 'side', gap: true },
    question: 'Should it go all the way and touch the roof?',
    options: [YES, NO],
    answer: 'no',
    right: 'Right: close, but not touching. A tiny gap lets the sound through.',
    wrong: 'If it touches, the sound stops. It goes close, but leaves a tiny gap.',
    after: { shape: at(1, 0, 0), shot: 'side', gap: true },
  },
  {
    id: 'sides',
    show: { shape: at(1, 0, 0), shot: 'threeQuarter' },
    question: 'Where do the sides of the tongue go: the back teeth or the front teeth?',
    options: [
      { id: 'back', label: 'Back teeth', says: ['back', 'back teeth', 'behind'] },
      { id: 'front', label: 'Front teeth', says: ['front', 'front teeth'] },
    ],
    answer: 'back',
    right: 'Yes! The sides press on the back teeth, like holding the tongue in place.',
    wrong: 'The front teeth are for other sounds. For r the sides press on the back teeth.',
    after: { shape: at(1, 1, 0), shot: 'threeQuarter', tour: true },
  },
  {
    id: 'tip',
    show: { shape: at(1, 1, 0), shot: 'side' },
    question: 'And the tip of the tongue: up or down?',
    options: [UP, DOWN],
    answer: 'down',
    right: 'Down: the tip stays low, behind the bottom teeth.',
    wrong:
      'Some people do lift the tip for r, and that works too. In this r the tip stays low, behind the bottom teeth.',
  },
  {
    id: 'lips',
    show: { shape: at(1, 1, 0), shot: 'side' },
    question: 'Last part: the lips. A little round, or a big kiss?',
    options: [
      { id: 'little', label: 'A little round', says: ['little', 'a little', 'small', 'bit'] },
      { id: 'kiss', label: 'A big kiss', says: ['kiss', 'big', 'lot'] },
    ],
    answer: 'little',
    right: 'Just a little. That is the whole r! Try it: "rrr".',
    wrong: 'A big kiss makes it sound like w. Just a little round. That is the whole r: "rrr".',
    after: { shape: partialR(FULL_R), shot: 'threeQuarter', tour: true },
  },
];

const PARTS: readonly Option[] = [
  {
    id: 'middle',
    label: 'The middle',
    says: ['middle', 'top', 'roof', 'touching', 'too high', 'too low'],
  },
  { id: 'tip', label: 'The tip', says: ['tip', 'point', 'front'] },
  { id: 'lips', label: 'The lips', says: ['lips', 'lip', 'kiss'] },
];

const FIX_TEXT: Readonly<Record<Mistake, { part: string; right: string; wrong: string }>> = {
  touching: {
    part: 'middle',
    right:
      'Yes: the middle touches the roof, so the sound would stop. It should stay just under it.',
    wrong: 'Look at the middle: it touches the roof. For r it stays just under it, not touching.',
  },
  tipUp: {
    part: 'tip',
    right:
      'Yes: the tip is up, touching behind the top teeth. That makes an l. For this r it stays low.',
    wrong: 'Look at the tip: it is up behind the top teeth, like an l. For this r it stays low.',
  },
  lips: {
    part: 'lips',
    right: 'Yes: the lips push out in a big kiss. That makes a w. For r they round only a little.',
    wrong: 'Look at the lips: a big kiss, like w. For r they round only a little.',
  },
};

/** One spot-the-mistake question per mistake. */
export const FIX: readonly Ask[] = (Object.keys(FIX_TEXT) as Mistake[]).map((m) => ({
  id: m,
  show: {
    shape: mistakeShape(m),
    shot: 'side',
    // Every one shows the dot (yellow, or red when touching), so the dot alone gives nothing away.
    gap: true,
  },
  question: 'This r has one mistake. Is it the middle, the tip, or the lips?',
  options: PARTS,
  answer: FIX_TEXT[m].part,
  right: FIX_TEXT[m].right,
  wrong: FIX_TEXT[m].wrong,
  after: { shape: partialR(FULL_R), shot: 'side', gap: true },
}));

/** Questions while the camera turns round a shape. */
export const SPIN: readonly Ask[] = [
  {
    id: 'r-touch',
    show: { shape: partialR(FULL_R), shot: 'side', tour: true, gap: true },
    question: 'Watch it turn. Is the tongue touching the roof of the mouth?',
    options: [YES, NO],
    answer: 'no',
    right: 'Right: it comes close, but there is a gap. This is the r.',
    wrong: 'Look at the yellow mark: there is a small gap. This is the r, close but not touching.',
  },
  {
    id: 'l-touch',
    show: { shape: pose('l'), shot: 'side', tour: true, gap: true },
    question: 'Watch it turn. Is the tongue touching the roof of the mouth?',
    options: [YES, NO],
    answer: 'yes',
    right: 'Yes: the tip touches behind the top teeth. This is an l, not an r.',
    wrong: 'Look at the red mark: the tip touches behind the top teeth. That is an l.',
  },
  {
    id: 'r-sides',
    show: { shape: partialR(FULL_R), shot: 'threeQuarter', tour: true, gap: true },
    question: 'Are the sides of the tongue pressing on the back teeth?',
    options: [YES, NO],
    answer: 'yes',
    right: 'Yes: the sides press on the back teeth. That holds the tongue up for r.',
    wrong: 'Look again at the back teeth: the sides of the tongue press on them. That is the r.',
  },
  {
    id: 'r-tip',
    // No turn: the tip hides behind the teeth when the camera turns toward the front.
    show: { shape: partialR(FULL_R), shot: 'side', gap: true },
    question: 'Look at the front of the tongue. Is the tip up or down?',
    options: [UP, DOWN],
    answer: 'down',
    right: 'Down, behind the bottom teeth. The middle does the lifting.',
    wrong: 'The middle is up, but the tip is down, behind the bottom teeth.',
  },
  {
    id: 'w-lips',
    show: { shape: pose('w'), shot: 'threeQuarter', tour: true, gap: true },
    question: 'Are the lips pushed far out?',
    options: [YES, NO],
    answer: 'yes',
    right: 'Yes: a big round "oo". This is a w. For r they round only a little.',
    wrong: 'They are pushed out, like "oo". This is a w; for r they round only a little.',
  },
];

/** `n` items from `items` in a random order (all when n is larger). */
export function deal<T>(items: readonly T[], n: number, random: () => number = Math.random): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j] as T, copy[i] as T];
  }
  return copy.slice(0, n);
}

/** What the child did in an activity, counted honestly. */
export interface Tally {
  asked: number;
  /** Right on the first answer. */
  firstTry: number;
}

/**
 * The end-of-activity line: what they did, in numbers, without inflating it. Praise goes to the
 * effort and to what was learned, never "perfect" unless it was.
 */
export function tallyLine(t: Tally): string {
  const n = t.asked;
  const q = n === 1 ? 'question' : 'questions';
  if (n === 0) return 'Come back any time to try one.';
  if (t.firstTry === n) return `You answered ${String(n)} ${q}, all right the first time.`;
  if (t.firstTry === 0)
    return `You answered ${String(n)} ${q}. These were tricky, and now you have seen the right shape for each one.`;
  const rest = n - t.firstTry === 1 ? 'the other one' : 'the others';
  return `You answered ${String(n)} ${q}: ${String(t.firstTry)} right the first time, and you saw why for ${rest}.`;
}
